// =============================================================
//  mayaApi — Appel sécurisé à la Cloud Function HTTP 'chatWithMaya'
//
//  - Envoie l'ID Token Firebase Auth dans Authorization: Bearer
//  - Gère les codes d'erreur métier (401 unauthenticated, 429 quota)
//  - Fallback statique si la fonction est indisponible (réseau)
// =============================================================

import { getAuth } from 'firebase/auth';
import app from '../../private/firebaseConfig';
import { getMayaResponse } from './mayaResponses';

const MAYA_URL =
  'https://us-central1-myconcours-ec9cb.cloudfunctions.net/chatWithMaya';

const REQUEST_TIMEOUT_MS = 60000;

const fetchWithTimeout = async (url, options, ms) => {
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), ms);
  try {
    return await fetch(url, { ...options, signal: ctrl.signal });
  } finally {
    clearTimeout(t);
  }
};

/**
 * Récupère l'ID Token de l'utilisateur courant.
 * Retourne null si pas connecté.
 */
const getIdToken = async () => {
  const auth = getAuth(app);
  const user = auth.currentUser;
  if (!user) return null;
  try {
    return await user.getIdToken(false);
  } catch {
    return null;
  }
};

/**
 * Appelle Desk AI via Claude (Cloud Function HTTP).
 * @param {Array<{role:'user'|'assistant', content:string}>} messages
 * @returns {Promise<{
 *   reply: string,
 *   source: 'claude'|'fallback'|'quota'|'unauth',
 *   remaining?: number,
 *   limit?: number,
 *   resetIso?: string,
 * }>}
 */
export const askMaya = async (messages) => {
  const idToken = await getIdToken();
  if (!idToken) {
    return {
      reply:
        "Pour discuter avec moi, tu dois d'abord te connecter à ton compte MyConcours 🙏",
      source: 'unauth',
    };
  }

  try {
    const resp = await fetchWithTimeout(
      MAYA_URL,
      {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({ messages }),
      },
      REQUEST_TIMEOUT_MS
    );

    // Quota dépassé : réponse spéciale, pas de fallback statique
    if (resp.status === 429) {
      const data = await resp.json().catch(() => ({}));
      return {
        reply:
          "Tu as atteint ta limite de 3 messages pour aujourd'hui 🌙\n\n" +
          "Reviens demain matin pour continuer à discuter avec moi ! " +
          "Pour une aide urgente, contacte le support via WhatsApp.",
        source: 'quota',
        remaining: 0,
        limit: data?.limit ?? 3,
        resetIso: data?.resetIso,
      };
    }

    if (resp.status === 401) {
      return {
        reply:
          "Ta session a expiré. Reconnecte-toi pour continuer à discuter avec moi 🙏",
        source: 'unauth',
      };
    }

    if (!resp.ok) {
      const text = await resp.text().catch(() => '');
      throw new Error(`HTTP ${resp.status} ${text.slice(0, 120)}`);
    }

    const json = await resp.json();
    const reply = json?.reply;
    if (typeof reply === 'string' && reply.trim().length > 0) {
      return {
        reply: reply.trim(),
        source: 'claude',
        remaining: json?.remaining,
        limit: json?.limit,
        resetIso: json?.resetIso,
      };
    }
    throw new Error('Empty reply');
  } catch (err) {
    if (__DEV__) {
      console.warn(
        '[DeskAI] Cloud Function indisponible, fallback statique. Détail:',
        err?.message || err
      );
    }
    const last = [...messages].reverse().find((m) => m.role === 'user');
    const reply = getMayaResponse(last?.content || '');
    return { reply, source: 'fallback' };
  }
};
